import { useEffect, useRef, useState } from "react";
import { DEFAULT_TEXTBOX_MAX_LENGTH } from "../../classes/constants/validationConstants";
import { ButtonSize } from "../../classes/enums/ButtonEnum";
import {
  createSanitizedHandler,
  INPUT_PATTERNS,
} from "../../classes/utils/ValidationUtils";
import "../../errorAnimations.css";
import useTranslation from "../../hooks/i18n/useTranslation";
import IconButton from "../iconButton/IconButton";
import "./TextBox.css";

const TextBox = ({
  placeholder = "",
  value = "",
  name = "",
  onChange = () => {},
  mandatory = false,
  type = "text",
  icon = null,
  onIconClick = null,
  editable = true,
  error = null,
  maxLength = DEFAULT_TEXTBOX_MAX_LENGTH,
  allowedPattern = INPUT_PATTERNS.ALL,
  isSanitized = false,
  onValidationError = null,
  validationPulseKey = 0,
  suffix = "",
  onBlur = null,
}) => {
  const { t } = useTranslation();
  const [animateError, setAnimateError] = useState(false);
  const pulseRef = useRef(validationPulseKey);
  const hasSuffix = !!suffix;

  useEffect(() => {
    if (error) {
      setAnimateError(false);
      requestAnimationFrame(() => setAnimateError(true));
      return;
    }
    if (validationPulseKey !== pulseRef.current) {
      pulseRef.current = validationPulseKey;
      setAnimateError(false);
      requestAnimationFrame(() => setAnimateError(true));
      return;
    }
    setAnimateError(false);
  }, [error, validationPulseKey]);

  const handleAnimationEnd = () => {
    setAnimateError(false);
  };

  const handleChange = isSanitized
    ? createSanitizedHandler(onChange, {
        allowedPattern,
        maxLength,
        onValidationError,
      })
    : onChange;

  return (
    <div className="App-textbox-container">
      {mandatory && (
        <span
          className={"App-text-medium App-styled-text App-textbox-mandatory"}
        >
          *{t("52")}
        </span>
      )}
      <div className="App-textbox-wrapper">
        <input
          id={name}
          type={type}
          value={value}
          name={name}
          className={`App-textbox ${
            icon ? "App-textbox-with-icon" : ""
          } ${hasSuffix ? "App-textbox-with-suffix" : ""} ${
            error ? "error-input" : ""
          }`}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={onBlur}
          disabled={!editable}
          maxLength={maxLength}
          aria-invalid={!!error}
          onAnimationEnd={handleAnimationEnd}
          style={{
            animation: animateError
              ? "errorInputPulse 0.35s ease-in-out 0s 2"
              : "none",
          }}
        />
        {hasSuffix && <span className="App-textbox-suffix">{suffix}</span>}
        {icon && onIconClick && (
          <IconButton
            icon={icon}
            onClick={onIconClick}
            size={ButtonSize.SMALL}
            isCircle={true}
            noBgColor={true}
            aria-label={`${name} icon button`}
            aria-labelledby={name}
            className="App-textbox-icon-button"
          />
        )}
      </div>
    </div>
  );
};

export default TextBox;
